import { setPersistence, browserLocalPersistence, signInWithEmailAndPassword, signOut, onAuthStateChanged } from 'firebase/auth';
import { auth } from '../firebase';

const MAX_ATTEMPTS = 5;
const LOCK_DURATION_MS = 60 * 1000; // 1 minute
const STORAGE_KEY = 'admin_signin_security';

export function isValidEmail(email) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email ?? '');
}

export function getLockState() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { attempts: 0, lockUntil: 0, locked: false, remainingMs: 0 };
    const data = JSON.parse(raw);
    const now = Date.now();
    const locked = data.lockUntil && now < data.lockUntil;
    const remainingMs = locked ? data.lockUntil - now : 0;
    return { attempts: data.attempts || 0, lockUntil: data.lockUntil || 0, locked, remainingMs };
  } catch {
    return { attempts: 0, lockUntil: 0, locked: false, remainingMs: 0 };
  }
}

export function recordFailedAttempt() {
  const state = getLockState();
  const attempts = (state.attempts || 0) + 1;
  const lockUntil = attempts >= MAX_ATTEMPTS ? Date.now() + LOCK_DURATION_MS : 0;
  const payload = { attempts, lockUntil };
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch {}
  return getLockState();
}

export function clearAttempts() {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {}
}

export function sanitizeAuthError(err) {
  const code = err?.code || '';
  const map = {
    'auth/user-not-found': 'Invalid email or password.',
    'auth/wrong-password': 'Invalid email or password.',
    'auth/invalid-email': 'Please enter a valid email address.',
    'auth/too-many-requests': 'Too many attempts. Please try again later.',
    'auth/network-request-failed': 'Network issue. Please try again.',
    'auth/user-disabled': 'Account disabled. Contact support.',
  };
  return map[code] || 'Invalid email or password.';
}

async function ensureSessionPersistence() {
  // Persist across page reloads and browser restarts in the same device
  await setPersistence(auth, browserLocalPersistence);
}

export async function signInAdmin(email, password) {
  try {
    await ensureSessionPersistence();
    await signInWithEmailAndPassword(auth, email, password);
    // No Firestore-based admin check; accept any signed-in user
    return { ok: true };
  } catch (err) {
    console.warn('Auth error:', err?.code || err?.message);
    return { ok: false, errorMsg: sanitizeAuthError(err) };
  }
}

export function onAuthStateChange(callback) {
  return onAuthStateChanged(auth, callback);
}